import { http, HttpResponse } from 'msw'

import { allSales } from 'app/mocks/data/transactions/sales'

import {
    api,
    isNumeric,
    queryIntoArrayNumber,
    SEARCH_PARAM_LIMIT,
    SEARCH_PARAM_OFFSET,
} from 'app/utils/constants'
import { searchParametersSaleApi } from 'app/utils/transactions/sale'

import type { Sale, SalesRequest } from 'app/types/transactions/sale'

export const getAllSales = http.get(
    `${process.env.REACT_APP_API_ADDRESS}/${api.transactions.sales.LIST}`,
    ({ request }) => {
        const { searchParams } = new URL(request.url)
        const limitQuery = searchParams.get(SEARCH_PARAM_LIMIT)
        const offsetQuery = searchParams.get(SEARCH_PARAM_OFFSET)

        const managersQuery = searchParams.get(searchParametersSaleApi.MANAGERS)
        const clientsQuery = searchParams.get(searchParametersSaleApi.CLIENTS)
        const sitesQuery = searchParams.get(searchParametersSaleApi.SITES)

        const terminalsQuery = searchParams.get(
            searchParametersSaleApi.TERMINALS,
        )

        const rangeMinQuery = searchParams.get(
            searchParametersSaleApi.AMOUNT_MIN,
        )

        const rangeMaxQuery = searchParams.get(
            searchParametersSaleApi.AMOUNT_MAX,
        )

        const dateStartQuery = searchParams.get(
            searchParametersSaleApi.DATE_START,
        )

        const dateEndQuery = searchParams.get(searchParametersSaleApi.DATE_END)
        const saleQuery = searchParams.get(searchParametersSaleApi.SALE)
        const cardQuery = searchParams.get(searchParametersSaleApi.CARD)
        const konviveQuery = searchParams.get(searchParametersSaleApi.KONVIVE)

        const paymentMethodsQuery = searchParams.get(
            searchParametersSaleApi.PAYMENT_METHOD,
        )

        const saleStatusQuery = searchParams.get(searchParametersSaleApi.STATUS)

        const disputeStatusQuery = searchParams.get(
            searchParametersSaleApi.DISPUTE_STATUS,
        )

        const hasIcouponQuery = searchParams.get(
            searchParametersSaleApi.HAS_ICOUPONS,
        )

        let allSalesFiltered: Sale[] = allSales

        // Manager filter
        if (managersQuery) {
            const managerIdsSplit = queryIntoArrayNumber(managersQuery)

            allSalesFiltered = allSalesFiltered.filter((sale) =>
                managerIdsSplit.includes(
                    sale.terminal.site.customer_name.client.id,
                ),
            )
        }

        // Client filter
        if (clientsQuery) {
            const clientIdsSplit = queryIntoArrayNumber(clientsQuery)

            allSalesFiltered = allSalesFiltered.filter((sale) =>
                clientIdsSplit.includes(sale.terminal.site.customer_name.id),
            )
        }

        // Site filter
        if (sitesQuery) {
            const siteIdsSplit = queryIntoArrayNumber(sitesQuery)

            allSalesFiltered = allSalesFiltered.filter((sale) =>
                siteIdsSplit.includes(sale.terminal.site.id),
            )
        }

        // Terminal filter
        if (terminalsQuery) {
            const terminalIdsSplit = queryIntoArrayNumber(terminalsQuery)

            allSalesFiltered = allSalesFiltered.filter((sale) =>
                terminalIdsSplit.includes(sale.terminal.id),
            )
        }

        // Date start filter
        if (dateStartQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) => sale.date_time.split('T')[0] >= dateStartQuery,
            )
        }

        // Date end filter
        if (dateEndQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) => sale.date_time.split('T')[0] <= dateEndQuery,
            )
        }

        // Sale filter
        if (saleQuery) {
            allSalesFiltered = allSalesFiltered.filter((sale) =>
                sale.id.toString().includes(saleQuery),
            )
        }

        // Card filter
        if (cardQuery) {
            allSalesFiltered = allSalesFiltered.filter((sale) =>
                sale.pan.includes(cardQuery),
            )
        }

        // Konvive filter
        if (konviveQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) =>
                    sale?.konvive?.first_name.includes(konviveQuery) ||
                    sale?.konvive?.last_name.includes(konviveQuery),
            )
        }

        // Payment method filter
        if (paymentMethodsQuery) {
            allSalesFiltered = allSalesFiltered.filter((sale) =>
                paymentMethodsQuery.split(',').includes(sale.payment_method),
            )
        }

        // Sales status filter
        if (saleStatusQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) => sale.sale_ok.toString() === saleStatusQuery,
            )
        }

        // Dispute status filter
        if (disputeStatusQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) => disputeStatusQuery === sale.status_dispute,
            )
        }

        // Has iCoupon filter
        if (hasIcouponQuery) {
            const hasIcoupon = hasIcouponQuery === 'true'

            allSalesFiltered = allSalesFiltered.filter((sale) => {
                if (hasIcoupon) {
                    return sale.icoupons.length > 0
                }

                return sale.icoupons.length === 0
            })
        }

        // Get min and max total
        // eslint-disable-next-line unicorn/no-array-reduce
        const minTotal = allSalesFiltered.reduce(
            (min, sale) => Math.min(min, sale.total),
            Infinity,
        )

        // eslint-disable-next-line unicorn/no-array-reduce
        const maxTotal = allSalesFiltered.reduce(
            (max, sale) => Math.max(max, sale.total),
            -Infinity,
        )

        // Range minimum filter
        if (rangeMinQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) => sale.total >= Number(rangeMinQuery),
            )
        }

        // Range maximum filter
        if (rangeMaxQuery) {
            allSalesFiltered = allSalesFiltered.filter(
                (sale) => sale.total <= Number(rangeMaxQuery),
            )
        }

        // Get total revenue
        const revenue = allSalesFiltered.reduce(
            (sum, sale) => sum + sale.total,
            0,
        )

        // Pagination
        const limit = isNumeric(limitQuery) ? Number(limitQuery) : 10
        const offset = isNumeric(offsetQuery) ? Number(offsetQuery) : 0

        const limitArray = Math.min(allSalesFiltered.length, offset + limit)
        const results = allSalesFiltered.slice(offset, limitArray)

        const next =
            offset + results.length < allSalesFiltered.length
                ? 'again'
                : undefined

        return HttpResponse.json<SalesRequest>({
            next,
            results,
            min_total: minTotal,
            max_total: maxTotal,
            products_total: allSalesFiltered.length,
            sales_total: allSalesFiltered.length,
            ca_total: revenue,
        })
    },
)
